import type { MouseEventHandler } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { findParentTypeNode, findParentTypeNodeWithClassName } from '@/utils/UI/html-content';

import { ScrollContainerIdentifier } from './components/scrollContainer';

export const ChatListElementContainerClassName = 'chat-list-element-container';
export const ChatListElementIgnoreClassName = 'chat-list-element-ignore-popover';

export interface IProps {
  parentContainerElement: HTMLDivElement | null;
  popoverElement: HTMLDivElement | null;
}

export interface HoverElementInfo {
  x: number;
  y: number;
  url: string;
  element: HTMLElement;
}

export interface IResult {
  onMouseMove?: MouseEventHandler<HTMLDivElement>;
  hoveredElement?: HoverElementInfo;
}

export default function usePopover(props: IProps): IResult {
  const { popoverElement, parentContainerElement } = props;

  const [hoveredElement, setHoveredElement] = useState<HoverElementInfo>();
  const hoverElementRef = useRef<boolean>(false);
  const hoverPopoverRef = useRef<boolean>(false);

  const tryCleanHoveredElement = useCallback(() => {
    setTimeout(() => {
      if (hoverPopoverRef.current || hoverElementRef.current) {
        return;
      }

      if (!hoveredElement) {
        return;
      }

      setHoveredElement(undefined);
    }, 100);
  }, [hoveredElement]);

  useEffect(() => {
    const cb = () => {
      if (!hoverElementRef.current && !hoverPopoverRef.current) {
        return;
      }

      hoverElementRef.current = false;
      hoverPopoverRef.current = false;
      tryCleanHoveredElement();
    };

    const element = document.getElementById(ScrollContainerIdentifier);

    if (!element) {
      return;
    }

    element.addEventListener('scroll', cb);

    return () => {
      element.removeEventListener('scroll', cb);
    };
  }, [tryCleanHoveredElement]);

  useEffect(() => {
    if (!popoverElement) {
      return;
    }

    popoverElement.onmouseenter = () => {
      hoverElementRef.current = true;
    };

    popoverElement.onmouseleave = () => {
      hoverElementRef.current = false;
      tryCleanHoveredElement();
    };
  }, [popoverElement, tryCleanHoveredElement]);

  const findParentNode = useCallback((element: HTMLElement) => {
    // 有可能是 markdown，所以需要找到最近的一层的 最外层的 a/img
    let parentElement: HTMLElement | undefined = undefined;

    parentElement = findParentTypeNodeWithClassName({
      fromNode: element,
      className: ChatListElementContainerClassName
    });

    if (parentElement) {
      return parentElement;
    }

    return findParentTypeNode({
      fromNode: element,
      targetType: ['a', 'img'],
      ignoreClass: [ChatListElementIgnoreClassName]
    });
  }, []);

  const onAClick = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    const href = (e.currentTarget as HTMLAnchorElement)?.getAttribute('href') || '';

    href && window.open(href, '_blank');
  };

  const findElementNodeLink = useCallback((element: HTMLElement) => {
    if (element.nodeName.toLocaleLowerCase() === 'a') {
      element.removeEventListener('click', onAClick);
      element.addEventListener('click', onAClick);

      return element.getAttribute('href') || '';
    }

    if (element.nodeName.toLocaleLowerCase() === 'img') {
      return element.getAttribute('src') || '';
    }

    if (element.classList.contains(ChatListElementContainerClassName)) {
      return element.getAttribute('data-link');
    }

    return undefined;
  }, []);

  const onMouseLeave = useCallback(() => {
    hoverPopoverRef.current = false;
    tryCleanHoveredElement();
  }, [tryCleanHoveredElement]);

  const onMouseMove: MouseEventHandler<HTMLDivElement> = useCallback(
    (e) => {
      let element = e.target as HTMLElement | undefined;

      if (!parentContainerElement || !popoverElement) {
        onMouseLeave();

        return;
      }

      if (!(element instanceof HTMLElement)) {
        onMouseLeave();

        return;
      }

      element = findParentNode(element);

      if (!element) {
        onMouseLeave();

        return;
      }

      const link = findElementNodeLink(element);

      if (!link) {
        onMouseLeave();

        return;
      }

      const elementRect = element.getBoundingClientRect();
      const parentRect = parentContainerElement.getBoundingClientRect();

      const offsetY =
        elementRect.y -
        parentRect.y +
        parentContainerElement.scrollTop +
        // totalScrollTop +
        elementRect.height;

      const offsetX =
        elementRect.x -
        parentRect.x +
        parentContainerElement.scrollLeft +
        (elementRect.width - popoverElement.getBoundingClientRect().width);

      hoverPopoverRef.current = true;
      setHoveredElement({
        element: element,
        url: link,
        x: offsetX,
        y: offsetY
      });
    },
    [findElementNodeLink, findParentNode, onMouseLeave, parentContainerElement, popoverElement]
  );

  return { onMouseMove, hoveredElement };
}
